import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import PlanIcon from '@atlaskit/icon-lab/core/plan';
import IconTile from '@atlaskit/icon/icon-tile';
import Modal, { ModalBody, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import {
	AgentActionPill,
	ToolPill,
} from '../../../../../../common/ui/agent-actions/agent-action-pill/index.tsx';
import {
	isKnowledgeScopeEnabled,
	useKnowledgeProductsRender,
} from '../../../../../../controllers/knowledge-products-rendering/index.tsx';
import { AgentKnowledgeSection } from '../../../../../../controllers/knowledge-section/index.tsx';
import type {
	McpServerNode,
	McpToolNode,
	ScenarioNode,
} from '../../../../../../services/scenario-list/index.ts';

import type { scenarioModal_AtlassianConversationAssistantAgent$key } from './__generated__/scenarioModal_AtlassianConversationAssistantAgent.graphql.ts';
import messages from './messages.ts';

const styles = cssMap({
	divider: {
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
		marginTop: token('space.100'),
	},
	body: {
		paddingBlock: token('space.300'),
	},
});

const Divider = () => {
	return <Box xcss={styles.divider} />;
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => {
	return (
		<Stack space="space.100">
			<Heading size="xsmall" id={title}>
				{title}
			</Heading>
			{children}
		</Stack>
	);
};

type ScenarioModalProps = {
	scenario: ScenarioNode;
	onClose: () => void;
	cloudUrl?: string;
	scenarioRef: scenarioModal_AtlassianConversationAssistantAgent$key | null;
} & AssistanceServiceInputParam;

export const ScenarioModal = ({
	scenario,
	onClose,
	assistanceServiceParams,
	cloudUrl,
	scenarioRef,
}: ScenarioModalProps & AssistanceServiceInputParam): React.JSX.Element => {
	di();
	const { formatMessage } = useIntl();
	const { getKnowledgeRenderForAgent } = useKnowledgeProductsRender({ assistanceServiceParams });

	const scenarioFragment = useFragment<scenarioModal_AtlassianConversationAssistantAgent$key>(
		graphql`
			fragment scenarioModal_AtlassianConversationAssistantAgent on AgentStudioScenario {
				...knowledgeSection_AtlassianConversationAssistantAgent_AgentKnowledgeSection_scenarioRef
			}
		`,
		scenarioRef,
	);

	const validMcpServers =
		scenario.mcpServers?.filter((mcpServer): mcpServer is McpServerNode => mcpServer != null) ?? [];
	const validMcpTools =
		scenario.mcpTools?.filter((mcpTool): mcpTool is McpToolNode => mcpTool != null) ?? [];

	const hasMcpServers = validMcpServers.length > 0;
	const hasMcpTools = validMcpTools.length > 0;
	const scenarioKnowledgeContext = useMemo(() => ({ scenarios: [scenario] }), [scenario]);
	const knowledgeRender = useMemo(
		() => getKnowledgeRenderForAgent({ agent: scenarioKnowledgeContext, cloudUrl }),
		[cloudUrl, getKnowledgeRenderForAgent, scenarioKnowledgeContext],
	);
	return (
		<Modal onClose={onClose} shouldCloseOnEscapePress shouldCloseOnOverlayClick>
			<ModalHeader hasCloseButton>
				<ModalTitle>
					<Inline alignBlock="center" space="space.100">
						<IconTile icon={PlanIcon} label="" appearance="blue" size="medium" />

						<Heading size="medium">{scenario.name}</Heading>
					</Inline>
				</ModalTitle>
			</ModalHeader>
			<Divider />
			<ModalBody>
				<Stack space="space.300" xcss={styles.body}>
					<Section title={formatMessage(messages.instructionsLabel)}>
						{scenario.instructions?.length ? (
							<Box>
								<TextArea
									aria-labelledby={formatMessage(messages.instructionsLabel)}
									resize="vertical"
									minimumRows={7}
									value={scenario.instructions}
									readOnly
								/>
							</Box>
						) : (
							<Text>{formatMessage(messages.noInstructions)}</Text>
						)}
					</Section>

					<Section title={formatMessage(messages.knowledgeSourcesLabel)}>
						<Stack space="space.050">
							<AgentKnowledgeSection
								{...knowledgeRender.knowledgeSectionProps}
								currentCloudId={assistanceServiceParams.siteId}
								isForgeAgent={false}
								knowledgeProductsRender={knowledgeRender.knowledgeProductsRender}
								knowledgeScopeEnabled={isKnowledgeScopeEnabled(scenarioKnowledgeContext)}
								isDeepResearchEnabled={scenario.isDeepResearchEnabled}
								scenarioRef={scenarioFragment ?? null}
							/>
						</Stack>
					</Section>

					<Section title={formatMessage(messages.skillsLabel)}>
						{!scenario.actions?.length &&
						!scenario.tools?.length &&
						!hasMcpServers &&
						!hasMcpTools ? (
							<Text>{formatMessage(messages.noSkills)}</Text>
						) : (
							<Stack space="space.050">
								{scenario.actions?.map((action) => (
									<AgentActionPill
										key={action.actionKey}
										action={{
											...action,
											displayName: action.name,
											id: action.actionKey,
										}}
										isActive={true}
									/>
								))}

								{scenario.scenarioVersion === 2 &&
									scenario.tools?.map((tool) => (
										<AgentActionPill key={tool.id} action={tool} isActive={true} />
									))}

								{validMcpServers.map((mcpServer) => (
									<ToolPill
										key={mcpServer.id}
										displayName={mcpServer.displayName}
										iconUrl={mcpServer.iconUrl}
									/>
								))}

								{validMcpTools.map((mcpTool) => (
									<ToolPill
										key={mcpTool.id}
										displayName={mcpTool.displayName}
										iconUrl={mcpTool.iconUrl}
									/>
								))}
							</Stack>
						)}
					</Section>
				</Stack>
			</ModalBody>
		</Modal>
	);
};
